import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Droplet, AlertCircle, CheckCircle2, Calendar, Pencil } from "lucide-react";
import { format } from "date-fns";

const LIMITS = {
  min: 0.2,
  max: 1.0,
};

export default function ChlorineControl() {
  const { currentVenue, user, loading: venueLoading } = useVenue();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [locations, setLocations] = useState([]);
  const [newLocation, setNewLocation] = useState("");
  const [formData, setFormData] = useState({
    location: "",
    value: "",
    time: format(new Date(), "HH:mm"),
    observations: "",
    date: format(new Date(), "yyyy-MM-dd"),
  });

  useEffect(() => {
    if (currentVenue) {
      loadRecords();
      loadLocations();
    }
  }, [currentVenue]);

  const loadRecords = async () => {
    setLoading(true);
    const items = await hosys.entities.ChlorineRecord.filter(
      { venue_id: currentVenue.id },
      "-date"
    );
    setRecords(items);
    setLoading(false);
  };

  const loadLocations = async () => {
    const items = await hosys.entities.ChlorineRecord.filter(
      { venue_id: currentVenue.id }
    );
    const uniqueLocations = [...new Set(items.map(r => r.location).filter(Boolean))];
    setLocations(uniqueLocations);
  };

  const handleSave = async () => {
    if (!formData.value || !formData.location) return;

    const value = parseFloat(formData.value);
    const status = value >= LIMITS.min && value <= LIMITS.max ? "correct" : "incorrect";

    if (editingRecord) {
      await hosys.entities.ChlorineRecord.update(editingRecord.id, {
        location: formData.location,
        value,
        status,
        observations: formData.observations,
        date: formData.date,
        time: formData.time,
        min_limit: LIMITS.min,
        max_limit: LIMITS.max,
      });
    } else {
      await hosys.entities.ChlorineRecord.create({
        venue_id: currentVenue.id,
        location: formData.location,
        value,
        status,
        observations: formData.observations,
        responsible_email: user.email,
        responsible_name: user.full_name,
        date: formData.date,
        time: formData.time,
        min_limit: LIMITS.min,
        max_limit: LIMITS.max,
      });
    }

    setFormData({ location: "", value: "", time: format(new Date(), "HH:mm"), observations: "", date: format(new Date(), "yyyy-MM-dd") });
    setEditingRecord(null);
    setShowForm(false);
    await loadRecords();
    await loadLocations();
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      location: record.location,
      value: record.value,
      time: record.time || "",
      observations: record.observations || "",
      date: record.date,
    });
    setShowForm(true);
  };

  const handleAddLocation = () => {
    if (newLocation.trim() && !locations.includes(newLocation)) {
      setLocations([...locations, newLocation]);
      setFormData({ ...formData, location: newLocation });
      setNewLocation("");
    }
  };

  if (venueLoading) return <QuickLoader />;

  const today = records.filter(r => r.date === format(new Date(), "yyyy-MM-dd"));
  const correctToday = today.filter(r => r.status === "correct").length;
  const incorrectToday = today.filter(r => r.status === "incorrect").length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Control de Clor"
        subtitle="Lecturas de clor a l'aigua"
        actions={
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl"
          >
            <Plus className="w-4 h-4" /> Nova lectura
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="p-4 border-0 shadow-sm">
          <p className="text-xs text-gray-500 font-medium mb-1">Lectures avui</p>
          <p className="text-2xl font-bold text-gray-900">{today.length}</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm bg-emerald-50">
          <p className="text-xs text-emerald-600 font-medium mb-1">Correctes</p>
          <p className="text-2xl font-bold text-emerald-700">{correctToday}</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm bg-red-50">
          <p className="text-xs text-red-600 font-medium mb-1">Incorrectes</p>
          <p className="text-2xl font-bold text-red-700">{incorrectToday}</p>
        </Card>
      </div>

      {/* Records */}
      {loading ? (
        <QuickLoader />
      ) : records.length === 0 ? (
        <EmptyState
          icon={Droplet}
          title="Sense lectures de clor"
          description="Comença a registrar les lectures de clor de l'aigua."
          action={
            <Button onClick={() => setShowForm(true)} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2">
              <Plus className="w-4 h-4" /> Primera lectura
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {records.map(record => (
            <Card key={record.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Droplet className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{record.location}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-900">
                        {record.value.toFixed(2)} ppm
                      </span>
                        <Badge
                          variant="secondary"
                          className={
                            record.status === "correct"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }
                        >
                          {record.status === "correct" ? (
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                          ) : (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          )}
                          {record.status === "correct" ? "Correcte" : "Incorrecte"}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {format(new Date(record.date), "dd/MM/yyyy")} a las {record.time}
                      </p>
                      <p className="text-xs text-gray-500">
                        {record.responsible_name}
                      </p>
                    </div>
                  </div>
                  {record.observations && (
                    <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded-lg mt-2">
                      {record.observations}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(record)} className="gap-1 text-xs">
                    <Pencil className="w-3 h-3" /> Editar
                  </Button>
                  <p className="text-xs text-gray-400">{LIMITS.min} - {LIMITS.max} ppm</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) setEditingRecord(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Droplet className="w-5 h-5 text-blue-600" />
              {editingRecord ? "Editar lectura" : "Nova lectura de clor"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Punt de lectura *
              </label>
              <div className="flex gap-2">
                <select
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">Selecciona o crea ubicació</option>
                  {locations.map(loc => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  placeholder="Nova ubicació..."
                  value={newLocation}
                  onChange={(e) => setNewLocation(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddLocation()}
                />
                <Button
                  type="button"
                  onClick={handleAddLocation}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Valor de clor (ppm) *
              </label>
              <Input
                type="number"
                step="0.1"
                min="0"
                placeholder="0.0"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">
                Límits: {LIMITS.min} - {LIMITS.max} ppm
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data *
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hora
                </label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observacions
              </label>
              <Textarea
                placeholder="Afegeix qualsevol comentari o observació..."
                value={formData.observations}
                onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                className="h-24 resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Cancel·lar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.value || !formData.location}
              className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
            >
              {editingRecord ? "Actualitzar" : "Guardar lectura"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}